import { Component, inject, signal, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { XtConnection, XtPingResult } from '../../core/models';

// The platform's ONE XT connection: a single order-registration URL + Auth-Key shared by every merchant
// (per-merchant identity is only the locationId appended to the URL — "1 auth only"). LEGACY: online orders go to
// the store Edge, and orders are registered in XT with these credentials only while XT:LegacyHandoffEnabled is true.
// The connection is still used to push status (ready / out / complete) to orders XT already has. This page is where
// the credentials are set, rotated, and verified. The Auth-Key is write-only: the form never shows the stored value, and leaving the field
// blank keeps it — the payment-method editor's convention.
@Component({
    selector: 'app-xt-integration',
    standalone: true,
    imports: [CardModule, ButtonModule, InputTextModule, MessageModule, TagModule, TooltipModule, FormsModule, DatePipe],
    template: `
        <p-card header="XT Integration (legacy)">
            <p-message severity="warn" styleClass="mb-3">
                Legacy. Online orders go to the store Edge; this connection still updates the status of orders already in XT.
            </p-message>

            @if (loadError()) {
                <p-message severity="error" styleClass="mb-3">{{ loadError() }}</p-message>
            }

            <div class="flex flex-col gap-3 max-w-xl">
                <div class="flex flex-col gap-1">
                    <label for="xt-url" class="text-sm font-medium">Order API URL<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="The location ID is added to the end of this URL, so include the trailing separator." tooltipPosition="top"></i></label>
                    <input pInputText id="xt-url" [(ngModel)]="orderApiUrl"
                           placeholder="https://xt.example.com/api/orders/" />
                </div>
                <div class="flex flex-col gap-1">
                    <label for="xt-key" class="text-sm font-medium">Auth-Key
                        @if (connection()?.authKeySet) { <p-tag value="set" severity="success" styleClass="ml-2" /> }
                    </label>
                    <input pInputText id="xt-key" type="password" [(ngModel)]="authKey"
                           [placeholder]="connection()?.authKeySet ? '•••••• (unchanged)' : 'required'" />
                </div>
                <div class="flex flex-col gap-1">
                    <label for="xt-batch" class="text-sm font-medium">Batch number</label>
                    <input pInputText id="xt-batch" [(ngModel)]="batchNumber" placeholder="optional" class="w-40" />
                </div>

                @if (saveError()) { <p-message severity="error">{{ saveError() }}</p-message> }
                @if (saved()) { <p-message severity="success">Connection saved.</p-message> }

                <div class="flex items-center gap-2">
                    <p-button label="Save" icon="pi pi-save" [loading]="saving()" (onClick)="save()" />
                    <p-button label="Ping" icon="pi pi-wifi" severity="secondary" [outlined]="true"
                              [loading]="pinging()" [disabled]="!connection()?.configured" (onClick)="ping()" />
                    @if (connection()?.updatedAt) {
                        <span class="text-xs text-surface-500">Last updated {{ connection()!.updatedAt | date: 'medium' }}</span>
                    }
                </div>

                @if (pingResult(); as p) {
                    <p-message [severity]="p.reachable && p.authAccepted ? 'success' : 'error'">
                        {{ p.detail }}
                    </p-message>
                }
            </div>
        </p-card>
    `
})
export class XtIntegration implements OnInit {
    private readonly api = inject(MiddlewareApi);

    readonly connection = signal<XtConnection | null>(null);
    readonly loadError = signal<string | null>(null);
    readonly saveError = signal<string | null>(null);
    readonly saved = signal(false);
    readonly saving = signal(false);
    readonly pinging = signal(false);
    readonly pingResult = signal<XtPingResult | null>(null);

    orderApiUrl = '';
    authKey = '';
    batchNumber = '';

    ngOnInit(): void {
        this.reload();
    }

    private reload(): void {
        this.api.getXtConnection().subscribe({
            next: (c) => {
                this.connection.set(c);
                this.orderApiUrl = c.orderApiUrl ?? '';
                this.batchNumber = c.batchNumber ?? '';
            },
            error: (e) => this.loadError.set(this.describe(e))
        });
    }

    save(): void {
        this.saveError.set(null);
        this.saved.set(false);
        this.pingResult.set(null);
        if (!this.orderApiUrl.trim()) {
            this.saveError.set('Order API URL is required.');
            return;
        }
        this.saving.set(true);
        this.api.upsertXtConnection({
            orderApiUrl: this.orderApiUrl.trim(),
            authKey: this.authKey.trim() || null,   // blank keeps the stored key
            batchNumber: this.batchNumber.trim() || null
        }).subscribe({
            next: () => {
                this.saving.set(false);
                this.saved.set(true);
                this.authKey = '';
                this.reload();
            },
            error: (e) => {
                this.saving.set(false);
                this.saveError.set(this.describe(e));
            }
        });
    }

    ping(): void {
        this.pinging.set(true);
        this.pingResult.set(null);
        this.api.pingXtConnection().subscribe({
            next: (r) => { this.pingResult.set(r); this.pinging.set(false); },
            error: (e) => {
                this.pingResult.set({ reachable: false, authAccepted: false, detail: this.describe(e) });
                this.pinging.set(false);
            }
        });
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 403) return 'Forbidden — your role lacks the instances:manage permission.';
        const server = e?.error?.detail ?? e?.error?.message;
        return server ?? `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
